import Cookies from 'js-cookie';
import React, { createContext, useContext, useMemo } from 'react';
import UserInitializationContext from './user-initialization-context';

const SLATE_THEME_OVERRIDE_COOKIE = 'cvna-slate-theme-override';

const SLATE_HOME_URL = 'https://slate.auto';

const isSlateThemeOverrideEnabled = (): boolean =>
  process.env.NODE_ENV !== 'production' && Cookies.get(SLATE_THEME_OVERRIDE_COOKIE) === 'true';

// Local dev server only ('development', not 'test'/'production'): a Slate vehicle
// also drives the Slate skin so the theme is visible without the Slate domain or override cookie.
const isLocalDevelopment = (): boolean => process.env.NODE_ENV === 'development';

const getIsSlateSkin = (): boolean => {
  const slateDomain = process.env.SLATE_DOMAIN;
  if (!slateDomain) return false;
  return location.hostname.toLowerCase() === slateDomain.toLowerCase();
};

// Context-free Slate-skin detection for surfaces rendered outside SlateProvider
// (e.g. the generic error page via RouteError/ErrorBoundary). Only the signals
// that need no vehicle data or React context: the Slate domain and the non-prod
// override cookie. Impersonation/local-dev skin both require a Slate vehicle,
// which isn't available on those surfaces.
const getIsSlateSkinFromDomain = (): boolean => isSlateThemeOverrideEnabled() || getIsSlateSkin();

const isSlateInventoryType = (inventoryType?: string | null): boolean => {
  return inventoryType?.toLowerCase() === 'slate';
};

// The single source of truth for "is this a Slate vehicle", including the non-prod
// dev-panel override.
const getIsSlateVehicle = (inventoryType?: string | null): boolean =>
  isSlateThemeOverrideEnabled() || isSlateInventoryType(inventoryType);

interface SlateContextValue {
  isSlateSkin: boolean;
  isSlateVehicle: boolean;
  /**
   * False only while the summary-fed inventory type is still in flight (`inventoryType`
   * is `undefined`). `isSlateVehicle` starts false and can flip once the summary lands,
   * so full-page consumers that branch on it wait on this rather than flipping arms
   * mid-view. Defaults to true so provider-less entries (native webviews) act resolved.
   */
  isSlateVehicleResolved: boolean;
}

const SlateContext = createContext<SlateContextValue>({
  isSlateSkin: false,
  isSlateVehicle: false,
  isSlateVehicleResolved: true
});

interface SlateProviderProps {
  /** `undefined` = not yet known (summary in flight); `null` = known, no inventory type. */
  inventoryType?: string | null;
}

const SlateProvider: React.FC<React.PropsWithChildren<SlateProviderProps>> = ({
  inventoryType,
  children
}) => {
  const { isImpersonating } = useContext(UserInitializationContext);
  const value = useMemo(() => {
    const slateVehicle = isSlateInventoryType(inventoryType);
    return {
      isSlateSkin:
        getIsSlateSkinFromDomain() ||
        (isImpersonating && slateVehicle) ||
        (isLocalDevelopment() && slateVehicle),
      isSlateVehicle: getIsSlateVehicle(inventoryType),
      isSlateVehicleResolved: inventoryType !== undefined || isSlateThemeOverrideEnabled()
    };
  }, [inventoryType, isImpersonating]);

  return <SlateContext.Provider value={value}>{children}</SlateContext.Provider>;
};

const useSlate = () => useContext(SlateContext);

export default SlateContext;
export {
  SlateProvider,
  useSlate,
  getIsSlateSkinFromDomain,
  SLATE_THEME_OVERRIDE_COOKIE,
  SLATE_HOME_URL
};
